class SSEService {
  constructor() {
    this.eventSource = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 3000
    this.isConnected = false
    this.listeners = []
    this.baseURL = process.env.VUE_APP_BASE_URL
    this.currentAppInfo = null // Speichere App-Info für Reconnects
    this.currentAuthToken = null // Speichere Auth-Token für Reconnects
  }

  // Füge einen Listener hinzu für Maintenance-Status Updates
  addListener(callback) {
    this.listeners.push(callback)
  }

  // Entferne einen Listener
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback)
  }

  // Benachrichtige alle Listener
  notifyListeners(data) {
    this.listeners.forEach(callback => callback(data))
  }

  // Starte die SSE-Verbindung
  connect(appInfo = null, authToken = null) {
    if (this.eventSource) {
      this.disconnect()
    }

    // Speichere App-Info und Auth-Token für Reconnects
    if (appInfo) {
      this.currentAppInfo = appInfo
    }
    if (authToken) {
      this.currentAuthToken = authToken
    }

    try {
      let url = `${this.baseURL}/agency/app/auth/maintenanceStatus/stream`
      
      // Erstelle Parameter-Objekt
      const params = new URLSearchParams()
      
      // Füge App-Informationen hinzu
      if (this.currentAppInfo) {
        params.append('appName', this.currentAppInfo.name)
        params.append('appVersion', this.currentAppInfo.version)
        params.append('buildTimestamp', this.currentAppInfo.buildTimestamp)
        params.append('clientTimestamp', this.currentAppInfo.timestamp)
      }
      
      // Füge Auth-Token hinzu (falls verfügbar)
      if (this.currentAuthToken) {
        params.append('token', this.currentAuthToken)
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`
      }
      
      console.log('Connecting to SSE with app info:', this.currentAppInfo)
      console.log('Connecting to SSE with auth token:', this.currentAuthToken ? '***' : 'none')
      this.eventSource = new EventSource(url)

      this.eventSource.onopen = () => {
        console.log('SSE connection opened')
        this.isConnected = true
        this.reconnectAttempts = 0
      }

      this.eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          this.handleMaintenanceUpdate(data)
        } catch (error) {
          console.error('Error parsing SSE message:', error)
        }
      }

      this.eventSource.addEventListener('maintenance-status', (event) => {
        try {
          const data = JSON.parse(event.data)
          this.handleMaintenanceUpdate(data)
        } catch (error) {
          console.error('Error parsing maintenance-status event:', error)
        }
      })

      this.eventSource.onerror = (error) => {
        console.error('SSE connection error:', error)
        this.isConnected = false
        this.handleReconnect()
      }

    } catch (error) {
      console.error('Failed to create SSE connection:', error)
      this.handleReconnect()
    }
  }

  // Behandle Maintenance-Status Updates
  handleMaintenanceUpdate(data) {
    console.log('Maintenance status update received:', data)
    this.notifyListeners(data)
  }

  // Behandle Reconnection
  handleReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++
      console.log(`Attempting to reconnect SSE (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${this.reconnectDelay}ms`)
      
      setTimeout(() => {
        // Verwende gespeicherte App-Info und Auth-Token für Reconnect
        this.connect(this.currentAppInfo, this.currentAuthToken)
      }, this.reconnectDelay)
    } else {
      console.error('Max reconnection attempts reached. SSE connection failed.')
      // Fallback: Benachrichtige Listener über Verbindungsfehler
      this.notifyListeners({ 
        status: 'maintenance', 
        reason: 'connection_failed',
        message: 'Verbindung zum Server verloren. Wartungsmodus aktiviert.' 
      })
    }
  }

  // Aktualisiere SSE-Verbindung mit Auth-Token
  updateWithAuthToken(authToken) {
    console.log('Updating SSE connection with auth token:', authToken ? '***' : 'none')
    
    // Speichere Auth-Token
    this.currentAuthToken = authToken
    
    // Wenn bereits verbunden, reconnect mit neuem Token
    if (this.isConnected) {
      console.log('Reconnecting SSE with updated auth token')
      this.connect(this.currentAppInfo, this.currentAuthToken)
    }
  }

  // Trenne die SSE-Verbindung
  disconnect() {
    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
      this.isConnected = false
      console.log('SSE connection closed')
    }
  }

  // Prüfe ob verbunden
  isSSEConnected() {
    return this.isConnected && this.eventSource && this.eventSource.readyState === EventSource.OPEN
  }

  // Manuelle Status-Abfrage als Fallback
  async checkStatusManually() {
    try {
      const response = await fetch(`${this.baseURL}/agency/app/auth/maintenanceStatus`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(5000)
      })

      if (!response.ok) {
        return { status: 'maintenance', reason: 'http_error' }
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.warn('Manual status check failed:', error)
      return { status: 'maintenance', reason: 'network_error' }
    }
  }
}

// Singleton-Instanz
const sseService = new SSEService()

export default sseService
